import { useEffect, useRef } from "react";
import { idxWidgetSrc, isIdxConfigured } from "@/config/idx";

interface IdxWidgetProps {
  widgetId: string;
  className?: string;
  minHeight?: number;
  label?: string;
}

/**
 * Brand CSS injected INTO each IDX widget's Shadow DOM.
 *
 * IDX "New" widgets render as Web Components (e.g. <idx-listings-showcase>,
 * <idx-listings-carousel>) with an OPEN shadow root. Their default chrome
 * ships a light-grey header bar (#f2f3f4) and a bright-blue "View All" link
 * (#3ca5d6) in a sans-serif face — none of which match the site. Because
 * shadow DOM is style-encapsulated, page-level CSS can't touch it, so we
 * append this stylesheet directly into each widget's shadow root once it
 * upgrades. Selectors below are written against the widget's internal class
 * names and therefore only apply inside the shadow root.
 */
const IDX_SHADOW_BRAND_CSS = `
  /* Header row: kill the grey bar, right-align the action as a refined link */
  .idx-listings-showcase__header,
  .idx-listings-carousel__header {
    background: transparent !important;
    border-bottom: 1px solid rgba(20, 24, 31, 0.12) !important;
    padding: 0 0 14px 0 !important;
    margin-bottom: 24px !important;
    display: flex !important;
    justify-content: flex-end !important;
    align-items: center !important;
  }
  .idx-listings-showcase__header a,
  .idx-listings-carousel__header a {
    color: #6a421e !important;
    font-family: Georgia, "Times New Roman", serif !important;
    text-transform: uppercase !important;
    letter-spacing: 0.22em !important;
    font-size: 0.68rem !important;
    font-weight: 600 !important;
    text-decoration: none !important;
    transition: color 0.2s ease !important;
  }
  .idx-listings-showcase__header a:hover,
  .idx-listings-carousel__header a:hover {
    color: #b98328 !important;
  }

  /* Price + address in our serif for an upscale feel */
  .idx-property-card__listing-price {
    font-family: Georgia, "Times New Roman", serif !important;
    font-weight: 400 !important;
    letter-spacing: 0.01em !important;
    font-size: 1.5rem !important;
  }
  .idx-property-card__address {
    font-family: Georgia, "Times New Roman", serif !important;
    font-weight: 400 !important;
    letter-spacing: 0.02em !important;
  }

  /* Status ("Active" / "Under Contract") as a refined champagne caption */
  .idx-property-card__listing-status {
    color: #edbe6e !important;
    text-transform: uppercase !important;
    letter-spacing: 0.18em !important;
    font-size: 0.62rem !important;
    font-weight: 600 !important;
  }

  /* Deepen the image overlay so serif text stays legible */
  .idx-property-card__image--overlay {
    background: linear-gradient(to top, rgba(20, 24, 31, 0.85) 0%, rgba(20, 24, 31, 0.18) 55%, rgba(20, 24, 31, 0) 100%) !important;
  }

  /* Virtual-tour pill on brand */
  .idx-property-card__virtual-tour {
    background: rgba(20, 24, 31, 0.75) !important;
    color: #f5f3ef !important;
    text-transform: uppercase !important;
    letter-spacing: 0.14em !important;
    font-size: 0.6rem !important;
  }
`;

/**
 * SPA-safe IDX Broker widget mount.
 *
 * Dynamically injects the widget's `<script>` into a container div on mount
 * and removes it on unmount so route changes don't stack duplicates. Once the
 * widget's Web Component upgrades, brand CSS is injected into its shadow root.
 * Renders an on-brand placeholder when the widget ID or account subdomain
 * hasn't been configured yet.
 */
const IdxWidget = ({
  widgetId,
  className = "",
  minHeight = 300,
  label = "IDX Widget",
}: IdxWidgetProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const configured = isIdxConfigured(widgetId);

  useEffect(() => {
    if (!configured) return;
    const container = containerRef.current;
    if (!container) return;

    container.innerHTML = "";

    const scriptId = `idxwidgetsrc-${widgetId}`;
    const existing = document.getElementById(scriptId);
    if (existing?.parentNode) existing.parentNode.removeChild(existing);

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.charset = "UTF-8";
    script.id = scriptId;
    script.src = idxWidgetSrc(widgetId);
    script.async = true;
    container.appendChild(script);

    // Inject brand CSS into any IDX widget shadow roots as they upgrade.
    const injectInto = (host: Element): boolean => {
      const sr = (host as HTMLElement & { shadowRoot: ShadowRoot | null }).shadowRoot;
      if (!sr) return false;
      if (sr.getElementById("idx-brand-overrides")) return true;
      const style = document.createElement("style");
      style.id = "idx-brand-overrides";
      style.textContent = IDX_SHADOW_BRAND_CSS;
      sr.appendChild(style);
      return true;
    };
    const scan = () => {
      container.querySelectorAll("*").forEach((el) => {
        if (el.tagName.toLowerCase().startsWith("idx-")) injectInto(el);
      });
    };

    const observer = new MutationObserver(scan);
    observer.observe(container, { childList: true, subtree: true });
    // shadowRoot attachment isn't a light-DOM mutation, so also poll briefly.
    const poll = window.setInterval(scan, 500);
    const stopPoll = window.setTimeout(() => window.clearInterval(poll), 10000);

    return () => {
      observer.disconnect();
      window.clearInterval(poll);
      window.clearTimeout(stopPoll);
      if (script.parentNode) script.parentNode.removeChild(script);
      if (container) container.innerHTML = "";
    };
  }, [widgetId, configured]);

  if (!configured) {
    return (
      <div
        className={`flex items-center justify-center rounded-sm border border-dashed border-primary/50 bg-background px-6 py-10 text-center ${className}`}
        style={{ minHeight }}
      >
        <div className="max-w-md">
          <p className="eyebrow text-primary mb-3">IDX Integration</p>
          <p className="text-foreground/70 font-medium">
            IDX widget — add &lsquo;{label}&rsquo; widget ID in{" "}
            <code className="font-mono text-sm text-primary">
              src/config/idx.ts
            </code>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ minHeight }}
      aria-label={label}
    />
  );
};

export default IdxWidget;
